import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { UserProductWalletCoinDetails } from 'src/app/core/models/userProductWalletCoinDetails.model';
import { DEFAULT_FONT_FAMILY } from '../../core/constants/defaultFontFamily';
import * as echarts from 'echarts';
import { ContractService } from 'src/app/core/services/wallet/wallet.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@ngneat/transloco';

@UntilDestroy()
@Component({
    selector: 'app-wallet-composition-chart',
    templateUrl: './wallet-composition-chart.component.html',
    styleUrls: ['./wallet-composition-chart.component.scss'],
    standalone: false
})
export class WalletCompositionChartComponent implements OnInit, OnChanges {
  @Input() viewEntered!: boolean;
  @ViewChild('compositionChart', { static: true }) chartContainer!: ElementRef;
  public userProductWalletCoinDetails!: UserProductWalletCoinDetails | undefined;
  public busy: boolean = true;
  public chart!: echarts.ECharts | undefined;
  public parentWidth!: number | undefined;
  public parentHeight!: number | undefined;
  public defaultFontFamily: string = DEFAULT_FONT_FAMILY;

  constructor(private contractService: ContractService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    this.contractService.getUserProductWalletCoinsDetails()
    .pipe(untilDestroyed(this))
    .subscribe((dataTokenDetails: UserProductWalletCoinDetails | undefined) => {
      this.userProductWalletCoinDetails = dataTokenDetails;
      this.busy = false;
        const newWidth: number = this.chartContainer?.nativeElement!.parentElement.clientWidth || 0;
        const newHeight: number = this.chartContainer?.nativeElement!.parentElement.clientHeight || 0;
  
        if (newWidth !== 0 && newHeight !== 0) {
          this.parentWidth = newWidth;
          this.parentHeight = newHeight;
        }
  
        if (dataTokenDetails) {
          this.createChart(dataTokenDetails);
        }

        this.observeChartContainer();
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewEntered'].currentValue === changes['viewEntered'].previousValue) {
      return
    }
    if (!changes['viewEntered'].currentValue && this.chart) {
      this.chart.dispose()
      this.chart = undefined;
    }

    if (changes['viewEntered'].currentValue && this.userProductWalletCoinDetails && !this.chart ) {
      this.createChart(this.userProductWalletCoinDetails);
    }
  }

  private observeChartContainer(): void {
    if (!this.chartContainer?.nativeElement?.parentElement) {
      return;
    }
  
    const parentElement = this.chartContainer.nativeElement.parentElement;
  
    const resizeObserver = new ResizeObserver(() => {
      const newWidth = parentElement.clientWidth;
      const newHeight = parentElement.clientHeight;
  
      if (newWidth > 0 && newHeight > 0) {
        this.parentWidth = newWidth;
        this.parentHeight = newHeight;
  
        if (this.userProductWalletCoinDetails) {
          this.createChart(this.userProductWalletCoinDetails);
        }
      }
    });
  
    resizeObserver.observe(parentElement);
  }

  @HostListener('window:resize')
  onResize() {
    this.observeChartContainer();
  }

  createChart(userProductWalletCoinDetails: UserProductWalletCoinDetails | undefined) {
    this.chart?.dispose();
    if (!this.chartContainer || !userProductWalletCoinDetails?.category?.length) {
      return;
    }

    let chartData = userProductWalletCoinDetails.category.map(({ percentage, categoryName, color }) => ({
      value: parseFloat(percentage),
      name: this.translocoService.translate(categoryName),
      itemStyle: { color }
    }));

    this.chart = echarts.init(this.chartContainer.nativeElement, 'light', { width: this.parentWidth, height: this.parentHeight });

    this.chart.setOption({
      animation: true,
      series: [
        {
          type: 'pie',
          radius: ['60%', '70%'],
          avoidLabelOverlap: false,
          minAngle: 20,
          itemStyle: {
            borderRadius: 50,
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              formatter: (params: any) => {
                return `{nameStyle|${params.name}}\n{percentStyle|${params.data.value.toFixed(2)}%}`;
              },
              color: (params: any) => params.data.itemStyle.color,
              rich: {
                nameStyle: {
                    fontSize: 18,
                    fontWeight: 'normal',
                    fontFamily: this.defaultFontFamily
                },
                percentStyle: {
                    fontSize: 28,
                    fontWeight: 'bold',
                    fontFamily: this.defaultFontFamily,
                    lineHeight: 35
                }
              }
            }
          },
          labelLine: {
            show: false
          },
          data: chartData
        }
      ]
    });
    const highestValueIndex: number = chartData.reduce((highestValueIndex, currentItem, currentIndex, dataArray) => {
      return currentItem.value > dataArray[highestValueIndex].value ? currentIndex : highestValueIndex;
    }, 0);

    let initialHighlightDone: boolean = false;

      this.chart.on('finished', () => {
        if(!initialHighlightDone) {
          this.chart?.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: highestValueIndex
          });
        }
      });

    this.chart.on('mouseover', () => {
      if (!initialHighlightDone) {
        this.chart?.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: highestValueIndex
        });
        initialHighlightDone = true;
      }
    });
  }
}
